import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Compass, Save, Loader2, ArrowLeft, Plus, Trash2,
  Lightbulb, Scale, HandHeart, Eye, Lock, Users,
  TrendingUp, Gauge, CheckCircle2, Circle
} from 'lucide-react';
import { LegacyTimeline } from '@/components/legacy/LegacyTimeline';

const API_URL = window.location.origin;

const SUPPORT_TYPES = ['Feedback', 'Introductions', 'Resources', 'Prayer', 'Accountability', 'Strategic Support'];
const VISIBILITY = {
  circle: { label: 'Full Legacy Circle', icon: Users },
  witness_circle: { label: 'My Witness Circle only', icon: Eye },
  leaders: { label: 'Crystal & Dr. Nicole only', icon: Lock }
};
const KIND_META = {
  update: { label: 'Journey Update', icon: Lightbulb, badge: 'bg-forest/10 text-forest' },
  decision: { label: 'Decision', icon: Scale, badge: 'bg-blue-50 text-blue-700' },
  support_request: { label: 'Support Request', icon: HandHeart, badge: 'bg-terracotta/10 text-terracotta' }
};

const Field = ({ label, value, onChange, textarea, placeholder }) => (
  <div>
    <Label className="text-xs text-gray-500">{label}</Label>
    {textarea ? (
      <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={2} placeholder={placeholder} />
    ) : (
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    )}
  </div>
);

const ProjectEditor = ({ project, setProject, onSave, saving }) => (
  <div className="space-y-5">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Journey Name" value={project.name} onChange={(v) => setProject({ ...project, name: v })} placeholder="e.g., The Restoration House" />
      <Field label="Current Stage" value={project.current_stage} onChange={(v) => setProject({ ...project, current_stage: v })} placeholder="e.g., Vision & research" />
    </div>
    <Field label="Vision" textarea value={project.vision} onChange={(v) => setProject({ ...project, vision: v })} placeholder="What are you building?" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Purpose (why it matters)" textarea value={project.purpose} onChange={(v) => setProject({ ...project, purpose: v })} />
      <Field label="People Served" textarea value={project.people_served} onChange={(v) => setProject({ ...project, people_served: v })} />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Desired Impact" textarea value={project.desired_impact} onChange={(v) => setProject({ ...project, desired_impact: v })} />
      <Field label="Twelve-Month Outcome" textarea value={project.twelve_month_outcome} onChange={(v) => setProject({ ...project, twelve_month_outcome: v })} />
    </div>
    <div>
      <p className="font-medium text-forest text-sm mb-2">Quarterly Milestones</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {['q1', 'q2', 'q3', 'q4'].map((q, i) => (
          <Field key={q} label={`Quarter ${i + 1} (${['Aug – Oct', 'Nov – Jan', 'Feb – Apr', 'May – Jul'][i]})`} value={project.milestones?.[q]}
            onChange={(v) => setProject({ ...project, milestones: { ...project.milestones, [q]: v } })} />
        ))}
      </div>
    </div>
    <div>
      <p className="font-medium text-forest text-sm mb-2">Current Priorities</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[0, 1, 2].map(i => (
          <Field key={i} label={`Priority ${i + 1}`} value={project.priorities?.top_three?.[i]}
            onChange={(v) => {
              const top = [...(project.priorities?.top_three || ['', '', ''])];
              top[i] = v;
              setProject({ ...project, priorities: { ...project.priorities, top_three: top } });
            }} />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        <Field label="Current Obstacle" value={project.priorities?.obstacle} onChange={(v) => setProject({ ...project, priorities: { ...project.priorities, obstacle: v } })} />
        <Field label="Next Courageous Action" value={project.priorities?.next_action} onChange={(v) => setProject({ ...project, priorities: { ...project.priorities, next_action: v } })} />
        <Field label="Support Needed" value={project.priorities?.support_needed} onChange={(v) => setProject({ ...project, priorities: { ...project.priorities, support_needed: v } })} />
      </div>
    </div>
    <div className="flex flex-col sm:flex-row sm:items-end gap-3">
      <div className="flex-1">
        <Label className="text-xs text-gray-500">Who can see my journey page</Label>
        <Select value={project.visibility || 'circle'} onValueChange={(v) => setProject({ ...project, visibility: v })}>
          <SelectTrigger data-testid="project-visibility-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(VISIBILITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button onClick={onSave} disabled={saving} className="bg-forest hover:bg-forest/90 text-white" data-testid="project-save-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save My Journey
      </Button>
    </div>
  </div>
);

const ProjectDetail = ({ projectId, user, onBack }) => {
  const [data, setData] = useState(null);
  const [entry, setEntry] = useState({ kind: 'update', content: '', why: '', support_type: 'Feedback' });
  const [posting, setPosting] = useState(false);

  const fetchDetail = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/projects/${projectId}`);
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load journey');
      onBack();
    }
  };
  useEffect(() => { fetchDetail(); }, [projectId]);

  if (!data) return <div className="h-64 bg-sand rounded-xl animate-pulse" />;
  const p = data.project;
  const isOwner = p.user_id === user?.id;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const handleAddEntry = async () => {
    if (!entry.content.trim()) { toast.error('Write something first'); return; }
    setPosting(true);
    try {
      await axios.post(`${API_URL}/api/legacy/projects/me/entries`, entry);
      toast.success('Added to your journey log');
      setEntry({ ...entry, content: '', why: '' });
      fetchDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    } finally {
      setPosting(false);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/legacy/entries/${id}`);
      fetchDetail();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="project-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-forest" data-testid="project-back-btn">
        <ArrowLeft className="h-4 w-4" /> All Journeys
      </button>
      <Card className="card-base">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="h-12 w-12">
              <AvatarImage src={p.owner_avatar || undefined} />
              <AvatarFallback className="bg-forest text-white">{p.owner_name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-heading text-2xl font-bold text-forest">{p.name || 'Untitled Journey'}</h3>
              <p className="text-sm text-gray-500">{p.owner_name}{p.current_stage ? ` · ${p.current_stage}` : ''}</p>
              {(p.growth_percent != null || p.confidence != null) && (
                <div className="flex gap-2 mt-1.5 flex-wrap">
                  {p.growth_percent != null && <Badge variant="outline" className="text-forest border-forest/30">{p.growth_percent}% of vision</Badge>}
                  {p.confidence != null && <Badge variant="outline" className="text-terracotta border-terracotta/30">Confidence {p.confidence}/10</Badge>}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {[['Vision', p.vision], ['Purpose', p.purpose], ['People Served', p.people_served], ['Desired Impact', p.desired_impact], ['Twelve-Month Outcome', p.twelve_month_outcome]].map(([label, val]) => val && (
              <div key={label}>
                <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
                <p className="text-gray-700 mt-0.5">{val}</p>
              </div>
            ))}
          </div>
          {Object.values(p.milestones || {}).some(Boolean) && (
            <div className="mt-5">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Quarterly Milestones</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {['q1', 'q2', 'q3', 'q4'].map((q, i) => p.milestones?.[q] && (
                  <div key={q} className="bg-sand/50 rounded-xl p-3">
                    <p className="text-xs font-medium text-terracotta">Q{i + 1} <span className="text-gray-400 font-normal">· {['Aug – Oct', 'Nov – Jan', 'Feb – Apr', 'May – Jul'][i]}</span></p>
                    <p className="text-sm text-gray-700">{p.milestones[q]}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(p.priorities?.top_three || []).some(Boolean) && (
            <div className="mt-5 text-sm">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Current Priorities</p>
              <ul className="list-disc list-inside text-gray-700">
                {(p.priorities.top_three || []).filter(Boolean).map((t, i) => <li key={i}>{t}</li>)}
              </ul>
              {p.priorities?.next_action && !(p.courageous_actions || []).length && <p className="mt-2"><span className="font-medium text-forest">Next courageous action:</span> {p.priorities.next_action}</p>}
            </div>
          )}
          {(p.courageous_actions || []).length > 0 && (
            <div className="mt-5 text-sm">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Courageous Actions</p>
              <ul className="space-y-1">
                {p.courageous_actions.map(a => (
                  <li key={a.id} className={`flex items-center gap-2 ${a.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                    {a.completed ? <CheckCircle2 className="h-4 w-4 text-forest flex-shrink-0" /> : <Circle className="h-4 w-4 text-gray-300 flex-shrink-0" />}
                    {a.text}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <LegacyTimeline project={p} entries={data.entries} />

      {isOwner && (
        <Card className="card-base">
          <CardContent className="p-4 space-y-3">
            <div className="flex gap-2 flex-wrap">
              {Object.entries(KIND_META).map(([k, m]) => (
                <button key={k} onClick={() => setEntry({ ...entry, kind: k })}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${entry.kind === k ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500 hover:border-forest'}`}
                  data-testid={`entry-kind-${k}`}>
                  {m.label}
                </button>
              ))}
              {entry.kind === 'support_request' && (
                <Select value={entry.support_type} onValueChange={(v) => setEntry({ ...entry, support_type: v })}>
                  <SelectTrigger className="w-44 h-8 text-xs" data-testid="support-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SUPPORT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <Textarea value={entry.content} onChange={(e) => setEntry({ ...entry, content: e.target.value })}
              placeholder={entry.kind === 'decision' ? 'What did you decide?' : entry.kind === 'support_request' ? 'What support do you need?' : 'What progress happened?'}
              rows={2} data-testid="entry-content-input" />
            {entry.kind === 'decision' && (
              <Input value={entry.why} onChange={(e) => setEntry({ ...entry, why: e.target.value })} placeholder="Why did you make this decision?" data-testid="entry-why-input" />
            )}
            <Button onClick={handleAddEntry} disabled={posting} size="sm" className="bg-forest hover:bg-forest/90 text-white" data-testid="entry-submit-btn">
              {posting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Add to Log
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <div className="space-y-3">
        {data.entries.map(e => {
          const meta = KIND_META[e.kind] || KIND_META.update;
          return (
            <Card key={e.id} className="card-base" data-testid={`project-entry-${e.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge className={`${meta.badge} text-xs`}>{e.kind === 'support_request' ? `Support: ${e.support_type}` : meta.label}</Badge>
                    <span className="text-xs text-gray-400">{new Date(e.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  {(isOwner || isAdmin) && (
                    <button onClick={() => handleDeleteEntry(e.id)} className="text-gray-300 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
                <p className="text-gray-700 mt-2 whitespace-pre-wrap">{e.content}</p>
                {e.why && <p className="text-sm text-gray-500 mt-1 italic">Why: {e.why}</p>}
              </CardContent>
            </Card>
          );
        })}
        {data.entries.length === 0 && <p className="text-center text-sm text-gray-400 py-4">The progress log begins with the first entry.</p>}
      </div>
    </div>
  );
};

const JourneyPulse = ({ project, onUpdated }) => {
  const [growth, setGrowth] = useState(project.growth_percent ?? 0);
  const [confidence, setConfidence] = useState(project.confidence ?? 5);
  const [savingPulse, setSavingPulse] = useState(false);
  const [newAction, setNewAction] = useState('');
  const [addingAction, setAddingAction] = useState(false);

  useEffect(() => {
    setGrowth(project.growth_percent ?? 0);
    setConfidence(project.confidence ?? 5);
  }, [project.growth_percent, project.confidence]);

  const dirty = growth !== (project.growth_percent ?? 0) || confidence !== (project.confidence ?? 5);

  const savePulse = async () => {
    setSavingPulse(true);
    try {
      const res = await axios.put(`${API_URL}/api/legacy/projects/me/pulse`, { growth_percent: growth, confidence });
      toast.success('Journey pulse updated');
      onUpdated(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pulse');
    } finally {
      setSavingPulse(false);
    }
  };

  const toggleAction = async (a) => {
    try {
      const res = await axios.put(`${API_URL}/api/legacy/projects/me/actions/${a.id}`, { completed: !a.completed });
      if (!a.completed) toast.success('Courageous action complete!');
      onUpdated(res.data);
    } catch (error) {
      toast.error('Failed to update action');
    }
  };

  const addAction = async () => {
    if (!newAction.trim()) return;
    setAddingAction(true);
    try {
      const res = await axios.post(`${API_URL}/api/legacy/projects/me/actions`, { text: newAction.trim() });
      setNewAction('');
      toast.success('New courageous action added');
      onUpdated(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add action');
    } finally {
      setAddingAction(false);
    }
  };

  const actions = project.courageous_actions || [];

  return (
    <div className="mt-5 space-y-4" data-testid="journey-pulse">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-sand/50 rounded-xl p-4" data-testid="journey-growth-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5 text-terracotta" /> Journey Growth
            </p>
            <span className="font-heading text-xl font-bold text-forest" data-testid="journey-growth-value">{growth}%</span>
          </div>
          <Slider value={[growth]} min={0} max={100} step={5} onValueChange={([v]) => setGrowth(v)} data-testid="journey-growth-slider" />
          <p className="text-xs text-gray-400 mt-2 italic">How close am I to completing the vision I set for this year?</p>
        </div>
        <div className="bg-sand/50 rounded-xl p-4" data-testid="journey-confidence-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
              <Gauge className="h-3.5 w-3.5 text-terracotta" /> Journey Confidence
            </p>
            <span className="font-heading text-xl font-bold text-forest" data-testid="journey-confidence-value">{confidence}/10</span>
          </div>
          <Slider value={[confidence]} min={1} max={10} step={1} onValueChange={([v]) => setConfidence(v)} data-testid="journey-confidence-slider" />
          <p className="text-xs text-gray-400 mt-2 italic">How confident am I that I will achieve it?</p>
        </div>
      </div>
      {dirty ? (
        <Button size="sm" onClick={savePulse} disabled={savingPulse} className="bg-terracotta hover:bg-terracotta/90 text-white" data-testid="journey-pulse-save-btn">
          {savingPulse ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Update My Pulse
        </Button>
      ) : project.pulse_updated_at && (
        <p className="text-xs text-gray-400" data-testid="journey-pulse-updated">Pulse last updated {new Date(project.pulse_updated_at).toLocaleDateString()}</p>
      )}

      <div data-testid="courageous-actions">
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Next Courageous Actions</p>
        <div className="space-y-2">
          {actions.map(a => (
            <label key={a.id} className="flex items-start gap-2 cursor-pointer" data-testid={`courageous-action-${a.id}`}>
              <Checkbox checked={a.completed} onCheckedChange={() => toggleAction(a)} className="mt-0.5" data-testid={`courageous-action-checkbox-${a.id}`} />
              <span className={`text-sm ${a.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>{a.text}</span>
            </label>
          ))}
          {actions.length === 0 && <p className="text-sm text-gray-400">What is the one courageous step in front of you?</p>}
        </div>
        <div className="flex gap-2 mt-3">
          <Input
            value={newAction}
            onChange={(e) => setNewAction(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addAction()}
            placeholder="Add a new courageous action..."
            data-testid="courageous-action-input"
          />
          <Button size="sm" onClick={addAction} disabled={addingAction || !newAction.trim()} className="bg-forest hover:bg-forest/90 text-white h-9" data-testid="courageous-action-add-btn">
            {addingAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const LegacyProjects = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [myProject, setMyProject] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const fetchProjects = async () => {
    try {
      const [list, mine] = await Promise.all([
        axios.get(`${API_URL}/api/legacy/projects`),
        axios.get(`${API_URL}/api/legacy/projects/me`)
      ]);
      setProjects(list.data);
      setMyProject(mine.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };
  useEffect(() => { fetchProjects(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/legacy/projects/me`, myProject);
      setMyProject(res.data);
      setEditing(false);
      toast.success('Your Journey is saved');
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (selectedId) return <ProjectDetail projectId={selectedId} user={user} onBack={() => { setSelectedId(null); fetchProjects(); }} />;
  if (!myProject) return <div className="h-64 bg-sand rounded-xl animate-pulse" />;

  return (
    <div className="space-y-6" data-testid="legacy-projects">
      {/* My project */}
      <Card className="card-base border-forest/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Compass className="h-5 w-5 text-terracotta" />
              <h3 className="font-heading text-lg font-semibold text-forest">My Journey</h3>
              {myProject.name && <Badge variant="outline">{myProject.name}</Badge>}
            </div>
            <div className="flex gap-2">
              {myProject.name && (
                <Button variant="outline" size="sm" onClick={() => setSelectedId(myProject.id)} data-testid="my-project-view-btn">View Page</Button>
              )}
              <Button size="sm" onClick={() => setEditing(!editing)} className="bg-forest hover:bg-forest/90 text-white" data-testid="my-project-edit-btn">
                {editing ? 'Close' : myProject.name ? 'Edit' : 'Start a Journey'}
              </Button>
            </div>
          </div>
          {!editing && !myProject.name && (
            <p className="text-sm text-gray-500 mt-2">One journey. One year. Document the vision, the milestones, and the woman required to build it.</p>
          )}
          {!editing && <JourneyPulse project={myProject} onUpdated={setMyProject} />}
          {editing && <div className="mt-5"><ProjectEditor project={myProject} setProject={setMyProject} onSave={handleSave} saving={saving} /></div>}
        </CardContent>
      </Card>

      {/* Circle projects */}
      <div>
        <h3 className="font-heading text-lg font-semibold text-forest mb-3">Journeys in the Circle</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {projects.map(p => (
            <Card key={p.id} className="card-base cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedId(p.id)} data-testid={`project-card-${p.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={p.owner_avatar || undefined} />
                    <AvatarFallback className="bg-forest text-white">{p.owner_name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-medium text-forest truncate">{p.name || 'Untitled Journey'}</p>
                    <p className="text-xs text-gray-400 truncate">{p.owner_name}{p.current_stage ? ` · ${p.current_stage}` : ''}</p>
                  </div>
                </div>
                {p.vision && <p className="text-sm text-gray-600 mt-3 line-clamp-2">{p.vision}</p>}
                {(p.growth_percent != null || p.confidence != null) && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {p.growth_percent != null && <Badge variant="outline" className="text-forest border-forest/30" data-testid={`journey-card-growth-${p.id}`}>{p.growth_percent}% of vision</Badge>}
                    {p.confidence != null && <Badge variant="outline" className="text-terracotta border-terracotta/30" data-testid={`journey-card-confidence-${p.id}`}>Confidence {p.confidence}/10</Badge>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        {projects.length === 0 && <p className="text-center text-sm text-gray-400 py-4">No journeys shared yet — yours could be the first.</p>}
      </div>
    </div>
  );
};

export default LegacyProjects;
